import React, { useMemo, useState } from "react";

import { getOsmTileUrlTemplate } from "lib/consts/osmTiles";
import { useARC } from "providers/ARC/ARCContext";
import useFleetArcId from "lib/utils/useFleetArcId";
import useIsHttpSecure from "lib/utils/useIsHttpSecure";
import { twMerge } from "tailwind-merge";
import OsmMap from "components/map/OsmMap";

const DEFAULT_ZOOM = 16;

export interface OsmLocationMapProps {
    latitude: number;
    longitude: number;
    zoom?: number;
    className?: string;
    /** When false (default), pan/zoom are disabled for a static “taken here” card. */
    interactive?: boolean;
}

/**
 * Static OSM inset centered on a lat/lon (gallery photo location, etc.).
 * Tile URL matches MiniView / ControlPage GPS mini-map.
 */
const OsmLocationMap: React.FC<OsmLocationMapProps> = ({
    latitude,
    longitude,
    zoom = DEFAULT_ZOOM,
    className,
    interactive = false,
}) => {
    const { selectedARC } = useARC();
    const arcId = useFleetArcId();
    const isSecure = useIsHttpSecure();
    const [tilesUnavailable, setTilesUnavailable] = useState(false);

    const osmTileUrl = useMemo(
        () => getOsmTileUrlTemplate(selectedARC?.host, isSecure, arcId),
        [selectedARC?.host, isSecure, arcId]
    );

    const tileEventHandlers = useMemo(
        () => ({
            tileerror: () => setTilesUnavailable(true),
            tileload: () => setTilesUnavailable(false),
        }),
        []
    );

    if (!osmTileUrl) {
        return (
            <div
                className={twMerge(
                    "flex h-44 w-full items-center justify-center rounded-r5 border border-black80 bg-black100 text-h5 text-black40",
                    className
                )}
            >
                Map unavailable
            </div>
        );
    }

    return (
        <div
            className={twMerge(
                "relative h-44 w-full overflow-hidden rounded-r5 border border-black80 bg-black100",
                className
            )}
        >
            <OsmMap
                locations={[{ id: "location", latitude, longitude }]}
                tileUrl={osmTileUrl}
                zoom={zoom}
                interactive={interactive}
                tileEventHandlers={tileEventHandlers}
                mapKey={`${latitude},${longitude},${zoom}`}
                className="h-full w-full"
            />
            {tilesUnavailable ? (
                <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-black90/90 px-2 py-1 text-h5 text-black40">
                    Map tiles unavailable
                </div>
            ) : null}
        </div>
    );
};

export default OsmLocationMap;
